import { isValidBIC } from 'ibantools'
import * as R from 'ramda'

type BICs = {
  [bic: string]: { name: string; codes: string[] }
}
// Source: https://www.finanssiala.fi/wp-content/uploads/2021/03/Suomalaiset_rahalaitostunnukset_ja_BIC-koodit.pdf
export const BankAccountIdentifers: BICs = {
  HELSFIHH: { name: 'Aktia Pankki', codes: ['405', '497'] },
  BIGKFIH1: { name: 'Bigbank', codes: ['717'] },
  // TODO Bonum Pankki
  POPFFI22: {
    name: 'POP Pankit (POP)',
    codes: ['470', '471', '472', '473', '474', '475', '476', '477', '478'],
  },
  CITIFIHX: { name: 'Citibank', codes: ['713'] },
  DABAFIHH: { name: 'Danske Bank A/S, Suomen sivuliike', codes: ['34', '8'] },
  DNBAFIHX: { name: 'DNB Bank ASA, Finland Branch', codes: ['37'] },
  HANDFIHH: { name: 'Handelsbanken', codes: ['31'] },
  HOLVFIHH: { name: 'Holvi', codes: ['799'] },
  NDEAFIHH: { name: 'Nordea Pankki (Nordea)', codes: ['1', '2'] },
  OKOYFIHH: { name: 'OP Ryhmä', codes: ['5'] },
  ESSEFIHX: { name: 'Skandinaviska Enskilda Banken (SEB)', codes: ['33'] },
  SBANFIHH: { name: 'S-Pankki', codes: ['39', '36'] },
  SWEDFIHH: { name: 'Swedbank', codes: ['38'] },
  ITELFIHH: {
    name: 'Säästöpankkien Keskuspankki, Säästöpankit (Sp) ja Oma Säästöpankki',
    codes: [
      '715',
      '400',
      '402',
      '403',
      // Ramda from is from (inclusive) to to (exclusive) which means the range ends need to be +1 here.
      ...R.range(406, 409).map((n) => `${n}`),
      ...R.range(410, 413).map((n) => `${n}`),
      ...R.range(414, 422).map((n) => `${n}`),
      ...R.range(423, 433).map((n) => `${n}`),
      ...R.range(435, 453).map((n) => `${n}`),
      ...R.range(454, 465).map((n) => `${n}`),
      ...R.range(483, 494).map((n) => `${n}`),
      ...R.range(495, 497).map((n) => `${n}`),
    ],
  },
  VPAYFIH2: {
    name: 'Viva Payment Services S.A., Suomen sivuliike',
    codes: ['798'],
  },
  AABAFI22: { name: 'Ålandsbanken (ÅAB)', codes: ['6'] },
}

const codeToBic = R.mergeAll(
  Object.entries(BankAccountIdentifers).flatMap(([bic, rest]) => {
    return rest.codes.map((code) => ({ [code]: bic }))
  })
)

/** iban should be a valid iban, bicFromIban does not validate it. */
export const bicFromIban = (iban: string): string | null => {
  if (iban.length < 15 || !iban.startsWith('FI')) {
    return null
  }

  let code = iban.slice(4, 7)

  if (code[0] === '3') code = code.slice(0, 2)
  else if (code[0] === '4' || code[0] === '7') code = code.slice(0, 3)
  else code = code[0]

  return R.propOr(null, code, codeToBic)
}

export const bankNameByBic = (bic: string): string | null => {
  return R.has(bic, BankAccountIdentifers)
    ? BankAccountIdentifers[bic].name
    : null
}

export const isFinnishIban = (iban: string): boolean => iban.startsWith('FI')

export const resolveValidBic = (bic: string | null, iban: string): string => {
  if (isFinnishIban(iban)) {
    const finnishBic = bicFromIban(iban)
    if (finnishBic === null) {
      throw new Error('Invalid Finnish IBAN')
    }
    return finnishBic
  }
  if (bic === null || bic.length === 0) {
    throw new Error('BIC is required for non-Finnish IBAN')
  }
  if (!isValidBIC(bic)) {
    throw new Error('BIC is invalid')
  }
  return bic
}
